import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { TrendingUp, Eye, EyeOff } from 'lucide-react';

const BRAND_NAME = "Cashflow";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-side">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 bg-[#021d49] rounded-[4px] flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-bold font-heading text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>{BRAND_NAME}</span>
          </div>

          {/* Form */}
          <div className="animate-slide-up">
            <h1 className="text-3xl font-bold text-[#021d49] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome back
            </h1>
            <p className="text-stone-500 mb-8">
              Sign in to manage your cashflow
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-[4px]"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-stone-700">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-[4px] pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px] h-11"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    Signing in...
                  </span>
                ) : 'Sign in'}
              </Button>
            </form>

            <p className="mt-8 text-center text-stone-500">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#021d49] font-medium hover:underline" data-testid="register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="auth-hero">
        <img
          src="https://images.pexels.com/photos/12732218/pexels-photo-12732218.jpeg"
          alt="Abstract geometric stone shapes"
          className="hero-image w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-xl font-medium mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            "Finally, a tool that shows me exactly where my money goes."
          </p>
          <p className="text-white/70 text-sm">Sarah M., Small Business Owner</p>
        </div>
      </div>
    </div>
  );
}
